import { useEffect, useRef, useState } from 'react';
import { progresoDelTiron, siguienteProgreso, SIN_TIRON } from '../utils/tironParaActualizar';

/** Lo que dura el aviso «Actualizando…» como mínimo: un parpadeo no dice que pasó algo. */
const AVISO_MINIMO_MS = 600;

const scrollTopDeLaPagina = () => (document.scrollingElement || document.documentElement).scrollTop;

/**
 * «Tirar para actualizar» sobre la PÁGINA (la bandeja se desplaza con la ventana). La regla del gesto está en
 * `utils/tironParaActualizar.js`. Oyentes pasivos: no frenan el desplazamiento ni le quitan el rebote a iOS.
 * Lo usa sólo `inbox/IndicadorDeTiron`, para que cada movimiento repinte el aviso y no la bandeja entera.
 *
 * @param {() => Promise<unknown>} alActualizar
 * @returns {{ distancia: number, armado: boolean, actualizando: boolean }}
 */
export function useTironParaActualizar(alActualizar) {
  const [progreso, setProgreso] = useState(SIN_TIRON);
  const [actualizando, setActualizando] = useState(false);
  const inicio = useRef(null);
  const armado = useRef(false);
  const enCurso = useRef(false);
  const alActualizarRef = useRef(alActualizar);
  alActualizarRef.current = alActualizar;

  useEffect(() => {
    const reiniciar = () => { inicio.current = null; armado.current = false; setProgreso((p) => siguienteProgreso(p, SIN_TIRON)); };
    const alTocar = (e) => {
      if (enCurso.current || e.touches.length !== 1 || scrollTopDeLaPagina() > 0) { inicio.current = null; return; }
      inicio.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    };
    const alMover = (e) => {
      if (inicio.current == null) return;
      // Ya se desplaza, o es otro dedo: este toque deja de ser un tirón hasta el siguiente.
      if (e.touches.length !== 1 || scrollTopDeLaPagina() > 0) { reiniciar(); return; }
      const p = progresoDelTiron({
        inicioY: inicio.current.y, y: e.touches[0].clientY, inicioX: inicio.current.x, x: e.touches[0].clientX,
        scrollTop: scrollTopDeLaPagina(),
      });
      armado.current = p.armado;
      setProgreso((prev) => siguienteProgreso(prev, p));
    };
    const alSoltar = async () => {
      const toca = armado.current && !enCurso.current;
      reiniciar();
      if (!toca) return;
      enCurso.current = true;
      setActualizando(true);
      const desde = Date.now();
      try {
        await alActualizarRef.current();
      } catch (e) {
        console.error('[bandeja] tirar para actualizar:', (e && e.message) || e);
      } finally {
        const falta = AVISO_MINIMO_MS - (Date.now() - desde);
        if (falta > 0) await new Promise((r) => setTimeout(r, falta));
        enCurso.current = false;
        setActualizando(false);
      }
    };
    window.addEventListener('touchstart', alTocar, { passive: true });
    window.addEventListener('touchmove', alMover, { passive: true });
    window.addEventListener('touchend', alSoltar, { passive: true });
    window.addEventListener('touchcancel', reiniciar, { passive: true });
    return () => {
      window.removeEventListener('touchstart', alTocar);
      window.removeEventListener('touchmove', alMover);
      window.removeEventListener('touchend', alSoltar);
      window.removeEventListener('touchcancel', reiniciar);
    };
  }, []);

  return { ...progreso, actualizando };
}
